/**
 * Prospects-list API slice (mockup M4).
 *
 * GET /api/v1/crm/persons — dedicated joined endpoint (⑥ gate path (c):
 * NOT the generic platform_config queryEngine; see server/routes/crmPersons.ts
 * + STATUS.md ⑥ entry). Response mirrors the queryEngine shape so a
 * post-demo migration to platform_config (via the V019 view) is mechanical.
 *
 * instanceId passed EXPLICITLY (like packsApi) so it + tab/search/page land
 * in the RTK cache key — a no-reload instance/tab/search change refetches
 * instead of serving stale rows. baseQueryWithInstanceId sees params.instanceId
 * and does not double-append.
 */

import { baseApi } from "./baseApi";
// REF-3a: the Source filter spans the marketing + arrival union (composite column).
import type { SourceChannel } from "../../../lib/enums/sourceChannel";
import type { ReadinessStageValue } from "../../../lib/enums/readinessStage";

export interface ProspectRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  score: string | null;
  score_band: "hot" | "warm" | "cool" | "cold" | null;
  /** E2 Discovery stage of change; null = not assessed. Rendered only for
   *  tenants whose packs declare the discovery feature. */
  readiness_stage: ReadinessStageValue | null;
  stage_name: string | null;
  stage_slug: string | null;
  stage_type: "open" | "won" | "lost" | "closed" | null;
  stage_color: string | null;
  community_name: string | null;
  /** Share v1: TRUE when this row is visible only because it was SHARED into the
   *  viewer's community (not owned by it) → renders the "Shared from {community}" badge. */
  is_shared_in?: boolean;
  owner_name: string | null;
  last_activity_at: string | null;
  /** Never-contacted-bucket (D3): earliest upcoming uncompleted commitment, or
   *  null. Drives the "call booked {date}" tag on the Never-contacted tab. */
  next_commitment_at?: string | null;
  care_type: ProspectCareType | null;
  /** REF-3a: composite Source for the grid column = lead_source if set, else
   *  source_channel (the two never co-occur). Label via SOURCE_CHANNEL_LABELS
   *  with a raw fallback for off-enum values. */
  source: string | null;
  primary_influencer: {
    first_name: string | null;
    last_name: string | null;
    relationship_type: string;
  } | null;
  created_at: string;
  /** PLC W14 Inc-1 (D5): days in the current stage (Pipeline Aging's
   *  definition). Present ONLY while the Days in stage filter is active. */
  days_in_stage?: number;
}

export type ProspectTab =
  | "all"
  | "hot"
  | "my"
  | "stale"
  // Never-contacted-bucket: prospects with no HAPPENED activity (first touch),
  // split out of "stale". Server tab; same pre_tour scope as "stale".
  | "never_contacted"
  | "tour_scheduled"
  | "draft"
  | "followed"
  // Share v1 — a CLIENT-only view: maps to tab='all' + includeShared=1 (the server has
  // no 'shared' tab). Surfaces prospects shared INTO the viewer's community.
  | "shared";

export interface ProspectsResponse {
  data: ProspectRow[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  displayName: string;
}

/**
 * Filter-Param Contract v1 — ATOMIC-SET enums (Overview Depth Batch 3 +
 * Prospects Depth SW1 Day-1). Prospect-pipeline only; mirrors backend
 * enums.
 */
export type ProspectStageSlug =
  | "inquiry"
  | "connection"
  | "pre_tour"
  | "post_tour"
  | "reservation"
  | "move_in";

/** `cold` is in the conceptual enum but seed never emits it (would
 *  always-empty the filter). See STATUS-prospects.md GREP C. */
export type ProspectScoreBand = "hot" | "warm" | "cool";

/** V025-locked enum mirror. */
export type ProspectCareType =
  | "independent_living"
  | "assisted_living"
  | "memory_care"
  | "skilled_nursing"
  | "respite";

// REF-3a: ProspectLeadSource (the 5-value marketing enum) was removed — the
// Source filter widened to the marketing+arrival union (SourceChannel), so the
// type had zero remaining consumers. The 5 values still live in lib/enums/leadSource.

export interface ProspectsArgs {
  instanceId: string;
  tab: ProspectTab;
  search?: string;
  page?: number;
  limit?: number;
  /** Filter-Param Contract ATOMIC-SET — pipeline stage slug(s).
   *  Single value is wrapped into a one-element array; back-compat with
   *  pre-widening `?stage=<slug>` URLs is preserved on the server. */
  stage?: ProspectStageSlug[];
  /** Filter-Param Contract ATOMIC-SET — lead-score band(s). */
  scoreBand?: ProspectScoreBand[];
  /** Filter-Param Contract ATOMIC-SET — care type(s). Runtime-editable vocab
   * (SW-CRM-VOCAB) → any slug, not just the canonical pack union. */
  careType?: string[];
  /** Filter-Param Contract ATOMIC-SET — lead source(s). */
  /** REF-3a: widened to the marketing+arrival union so the Source filter matches
   *  the composite Source column (the backend predicates on the same COALESCE). */
  leadSource?: SourceChannel[];
  /** Filter-Param Contract ATOMIC-SET — readiness stage-of-change (E2
   *  Discovery, D9): the 4 stage codes + 'not_assessed' (IS NULL predicate).
   *  Widened to string so the sentinel rides the same set plumbing. */
  readinessStage?: string[];
  /** Filter-Param Contract ATOMIC-SET — E3 SLA job-computed flag (V111):
   *  'first_response_breached' | 'stalled' | 'gone_quiet'. Feature-gated in the
   *  UI (pack "sla_alerts", the E2 readiness idiom incl. URL-param ignore). */
  slaStatus?: string[];
  /**
   * Filter-Param Contract ATOMIC-SET — lifecycle status (closed-prospects):
   * 'active' | 'closed_lost' | 'deceased' | 'closed_won'. ABSENT means the
   * default OPEN list, which is why nothing changes for anyone who does not opt
   * in. `draft` is NOT a token — the Draft tab is the only way to express it.
   */
  status?: string[];
  /**
   * Filter-Param Contract ATOMIC-SET — close reason SLUG(s) (PLC W7 Inc-2). The
   * instance's own tenant-editable catalog, so any slug; the page sends it only
   * while a closed status is selected.
   */
  closeReason?: string[];
  /**
   * PLC W14 Inc-1 — Days in stage, inclusive bounds (`funnel_depth` only; the
   * page omits them without the feature). Forwarded verbatim: the server
   * validates (0..3650 integers; a malformed value or min > max matches nothing).
   */
  daysInStageMin?: string;
  daysInStageMax?: string;
  /**
   * Filter-Param Contract — ATOMIC-EXACT (boolean sentinel): persons with
   * no future-scheduled, not-yet-completed activity (NOT EXISTS predicate).
   */
  noNextActivity?: boolean;
  /**
   * Filter-Param Contract — community selection (single UUID). Narrows the read
   * scope to one community, bounded server-side by the caller's access ceiling.
   * Threaded by dashboard drill-downs so the list matches the filtered widget.
   */
  communityId?: string;
  /**
   * SW-CRM-PROSPECT Inc4 — view a static list: narrows to that list's members
   * (a membership EXISTS predicate), still bounded by the community read-filter.
   */
  listId?: string;
  /**
   * G15 — view a saved DYNAMIC segment: narrows to the rows matching that
   * segment's compiled criteria, still bounded by the community read-filter.
   *
   * ⚠️ LOAD-BEARING, and the key that was missed once already. `segmentId` is a
   * member of the Filter-Param contract, so the export, the labels run and every
   * bulk "Select All N" target pick it up from `currentFilterParams` on the page.
   * THE GRID DOES NOT — it is the one consumer built from these TYPED args
   * instead, so a param present in `currentFilterParams` and absent here makes
   * the grid disagree with its own export (the G15 UAT P1: chip applied, grid
   * unfiltered, export correct). Adding a filter param means adding it in BOTH
   * places; the "grid request carries the applied segment" regression test in
   * ProspectsPage.test.tsx is what holds the two halves together.
   */
  segmentId?: string;
  /**
   * Speed-to-Lead drill-through from the Overview. Same load-bearing rule as
   * `segmentId` above: it has to be declared HERE, destructured in the query, and
   * appended to `params`, or the grid silently ignores it.
   *
   * `speedClock` rides along because the two clocks bucket the same lead
   * differently — sending the bucket without the clock filters on a different
   * measurement than the tile the user clicked.
   */
  speedBucket?: string;
  speedClock?: string;
  speedPeriod?: string;
  /**
   * PLC W3 R12 — a drill from an Overview in page-range mode carries the page
   * range instead of `speedPeriod`. Forwarded only with a `speedBucket` (the only
   * read the server applies it to).
   */
  speedRange?: string;
  speedFrom?: string;
  speedTo?: string;
  /**
   * SW-CRM-PROSPECT Share v1 — the "Shared with me" tab. When true, the list ALSO
   * admits prospects SHARED into the caller's community (owner-only counts elsewhere
   * are unaffected). Off by default (the main list stays "what I own").
   */
  includeShared?: boolean;
}

// ── Person Detail (M3) ──────────────────────────────────────────────────────
export interface PersonDetail {
  id: string;
  first_name: string | null;
  last_name: string | null;
  // Day 22: editable ⇒ readable (Profile edit modal round-trips).
  salutation: string | null;
  preferred_name: string | null;
  age: number | null;
  score: string | null;
  score_band: ProspectRow["score_band"];
  /** Inc1: non-null ⇒ score_band was set MANUALLY (override marker; a future
   *  scorer skips overridden rows). Null ⇒ computed/seeded. */
  score_overridden_at: string | null;
  /** E5 Inc-1: the per-prospect nurture kill-switch (D6). TRUE ⇒ drip execution
   *  + auto-enrollment are halted for this prospect. */
  automation_paused: boolean;
  lifecycle_status: string | null;
  /** PLC W7 Inc-1 (11.1): the recorded close — reason name snapshot, id, date,
   *  the V233 note column, and the reason's details prompt. Null on an open prospect. */
  close_reason?: string | null;
  close_reason_id?: string | null;
  closed_at?: string | null;
  close_note?: string | null;
  close_reason_details_type?: string | null;
  close_reason_details_description?: string | null;
  /** PLC W7 Inc-3 (V237): the listed competitor a 'competitor' close named. */
  competitor_id?: string | null;
  competitor_name?: string | null;
  /** PLC W7 Inc-4 (V238): the detail option a 'choice' close picked (code + label). */
  close_reason_detail_code?: string | null;
  close_reason_detail_label?: string | null;
  /** Inc2: is the acting user following this prospect? */
  is_following: boolean;
  primary_email: string | null;
  primary_phone: string | null;
  community_id: string | null;
  community_name: string | null;
  /**
   * Share v1 — TRUE when this prospect reached you through a cross-community SHARE rather
   * than through ownership of its community. The server emits it FROM the read predicate
   * (the same expression the list row carries); never re-derive it from the ceiling on the
   * client. It is the detail page's read-only verdict: the prospect write routes refuse a
   * shared-in caller.
   */
  is_shared_in: boolean;
  pipeline_id: string | null;
  stage_id: string | null;
  stage_slug: string | null;
  stage_name: string | null;
  stage_type: ProspectRow["stage_type"];
  stage_color: string | null;
  stage_entered_at: string | null;
  owner_name: string | null;
  fields: Record<string, unknown> | null;
  /** Pack custom-field VALUES (V113), read-role-filtered by the server. */
  custom_data: Record<string, unknown>;
  created_at: string;
  last_activity_at: string | null;
  middle_name: string | null;
  gender: string | null;
  date_of_birth: string | null;
  preferred_contact_method: string | null;
  // Do-not-contact flags (item 3) — hydrate the profile-edit toggles + drive
  // the send enforcers (do_not_email / do_not_text are honored at send).
  do_not_call: boolean;
  do_not_text: boolean;
  do_not_email: boolean;
  do_not_mail: boolean;
  emails: Array<{ address?: string; type?: string; primary?: boolean }> | null;
  phones: Array<{ number?: string; type?: string; primary?: boolean }> | null;
  addresses: Array<Record<string, unknown>> | null;
  /** Day 11: linked Sky Advisor chat session (Day-10 link, derived on
   *  read). Powers Person Detail "View transcript →" → Inbox. */
  chat_session_id: string | null;
  /** Day 16: derived resident row id if this prospect converted to a
   *  resident (NULL otherwise). Powers the "View resident record →"
   *  cross-link. Same derived-on-read shape as chat_session_id.
   *  E21 Inc-3: the CURRENT stint (open if any, else latest closed). */
  resident_id: string | null;
  /** E21 Inc-3 (Decision 6): id of the person's OPEN residency stint
   *  (closed_at IS NULL), NULL when they have no live residency. The
   *  reserve/move-in affordance keys on THIS so a fully-moved-out person
   *  can be re-admitted ("Move In Again") via the reservation seam. */
  open_resident_id: string | null;
  /** The reserved/converted resident's intended move-in date (YYYY-MM-DD),
   *  captured at deposit time. Prefills the Move-In stage-transition modal so it
   *  stays uniform with Unit & Deposit. NULL when not reserved (modal → today). */
  reserved_move_in_date: string | null;
  /** The reservation deposit's received date (YYYY-MM-DD): the LOWER bound for
   *  a valid move-in date in the Move-In modal. NULL when no qualifying deposit. */
  reserved_received_on: string | null;
  /** The latest qualifying reservation deposit (received/cleared, unit-linked),
   *  so the Unit & Deposit section can re-display a recorded deposit after a
   *  refresh instead of resetting to an empty picker. NULL when none (or once the
   *  deposit is cancelled). */
  reservation_deposit:
    | ({
        deposit_id: string;
        unit_id: string;
        unit_number: string | null;
        amount: number;
        /** Inc1: total already refunded against this deposit. */
        amount_refunded: number;
        /** Inc1: remaining refundable (collected − applied − refunded − forfeited).
         *  The refund UI caps to this; the server re-validates. */
        refundable: number;
        status: "received" | "cleared";
        received_on: string | null;
        move_in_date: string | null;
        /** V219 — who holds the lease. Read back so the section can display AND
         *  correct it: set once at deposit time, a wrong answer used to be
         *  permanent, and it decides the Yardi privacy level that push-once makes
         *  final. */
        is_second_occupant: boolean;
      } & Partial<DepositQuotedTerms>)
    | null;
  /** PLC W9 Inc-5 — the OPEN stay's terms once a reservation exists (the stay
   *  owns them from then on). Present only with the senior-living `concessions`
   *  feature; null when there is no open stay (or it is outside the ceiling). */
  stay_terms?: StayTerms | null;
  /** Day 18: referrer attribution (primary_source_id → org, V019 FK).
   *  NULL for organic prospects. Powers the Person Detail
   *  "Referred by … → View referrer" affordance. */
  referred_by_id: string | null;
  referred_by_name: string | null;
  referred_by_type: string | null;
  /** REF-3a: derived PARTNER source name (lead_source_id → skycrm_lead_sources,
   *  V037 FK). Powers the Person Detail "Partner Source" slot. NULL when the
   *  prospect has no partner attribution. */
  partner_source_name: string | null;
  /** PLC W8 Inc-3 — present ONLY with `lead_source_taxonomy`: the Partner Source
   *  edit context. The live catalog row's id (null when none / removed), whether
   *  it is enabled (D19 "(disabled)"), its category (the D10 mismatch chip), and
   *  the ingest lock (D9: a partner-ingested source is read-only). */
  partner_source_id?: string | null;
  /** The stored source's catalog row was soft-deleted (the name is then null);
   *  the id still rides along so the edit can clear it. */
  partner_source_deleted?: boolean;
  partner_source_enabled?: boolean | null;
  partner_source_category?: string | null;
  partner_source_locked?: boolean;
}
/**
 * PLC W9 Inc-5 (V242) — the QUOTED rate and concessions on a reservation
 * deposit. On `reservation_deposit` only with the `concessions` feature (and
 * when the deposit's community is in the caller's ceiling); absent otherwise.
 */
export interface DepositQuotedTerms {
  monthly_rate: number | null;
  recurring_concession: number | null;
  one_time_concession: number | null;
  concessions_notes: string | null;
  recurring_concession_type_id: string | null;
  recurring_concession_type_label: string | null;
  one_time_concession_type_id: string | null;
  one_time_concession_type_label: string | null;
}

/** The six quoted-terms keys as a write body: omitted = untouched, null = clear. */
export type DepositTermsBody = Partial<{
  monthly_rate: number | null;
  recurring_concession: number | null;
  one_time_concession: number | null;
  concessions_notes: string | null;
  recurring_concession_type_id: string | null;
  one_time_concession_type_id: string | null;
}>;

/** PLC W9 Inc-5 — the open stay's terms, as the housing-terms editor needs them. */
export interface StayTerms extends DepositQuotedTerms {
  resident_id: string;
  assignment_id: string;
  unit_id: string;
  unit_number: string | null;
  stay_type: string | null;
  is_second_occupant: boolean;
}

export interface Influencer {
  /** The influencer PERSON id. NOT the link id — see link_id. */
  id: string;
  /**
   * The `skycrm_influencer_links` row id. This is what the decision-unit PATCH
   * addresses; `id` above is the linked person, and passing it as a link id
   * silently 404s (the WHERE matches no row).
   */
  link_id: string;
  first_name: string | null;
  last_name: string | null;
  relationship_type: string;
  decision_role: string | null;
  is_primary: boolean;
  primary_email: string | null;
  primary_phone: string | null;
  metadata: Record<string, unknown> | null;
}
export interface RecentActivity {
  id: string;
  activity_type: string;
  /** Vocab display name (JOINed skycrm_activity_types.name); null for hidden/legacy codes. */
  activity_type_label?: string | null;
  subject: string | null;
  body: string | null;
  direction: string | null;
  status: string | null;
  occurred_at: string;
  /**
   * Stage-transition rows only: the PERSISTED slugs plus live-resolved names,
   * so the Summary card's stage-change line follows a rename instead of
   * contradicting the Activities tab on the same screen. Consumed by
   * ActivityTimeline.resolveActivitySubject.
   */
  from_stage_slug?: string | null;
  from_stage_label?: string | null;
  to_stage_slug?: string | null;
  to_stage_label?: string | null;
}
export interface FunnelStage {
  id: string;
  slug: string;
  name: string;
  sort_order: number;
  stage_type: "open" | "won" | "lost" | "closed";
  color: string | null;
}
export interface PinnedContext {
  id: string;
  context_type: "objection" | "commitment" | "family_context" | "care_signal" | "urgency_signal";
  subtype: string | null;
  description: string;
  extracted_by: "ai" | "operator";
  confidence_score: string | null;
  resolved_at: string | null;
  pinned_at: string;
  created_at: string;
}
export interface PersonDetailResponse {
  person: PersonDetail;
  funnel: {
    stages: FunnelStage[];
    current_stage_id: string | null;
    stage_entered_at: string | null;
    days_in_current_stage: number | null;
  };
  pinned_context: PinnedContext[];
  recent_activities: RecentActivity[];
  influencers: Influencer[];
}

// ── Activities sub-resource (M5, Day 9) ─────────────────────────────
// Shape matches ActivityTimeline's TimelineActivity (structurally
// compatible — the API is the contract owner; the component accepts
// a structurally-equal shape). GET /crm/persons/:personId/activities.
export interface PersonActivity {
  id: string;
  activity_type: string;
  /** Vocab display name (JOINed skycrm_activity_types.name); null for hidden/legacy codes. */
  activity_type_label?: string | null;
  subject: string | null;
  body: string | null;
  direction: string | null;
  status: string | null;
  occurred_at: string;
  duration_seconds: number | null;
  outcome: string | null;
  outcome_label?: string | null;
  is_system_generated: boolean | null;
  has_transcript: boolean;
  // Increment B1 — Zoom meeting attachment, when present.
  channel_external_id?: string | null;
  zoom_join_url?: string | null;
  zoom_sync_status?: string | null;
  /**
   * RECORD-scoped profile pin (skycrm_activities.pinned) — the GLOBAL timeline
   * pin-to-top. Boolean on real activities; null on read-time-merged inbound SMS.
   */
  is_record_pinned?: boolean | null;
  /**
   * Stage-transition rows only: the PERSISTED slugs plus the stage names resolved
   * LIVE against the person's pipeline, so a rename reflects in the timeline
   * rather than leaving the prerendered `subject` frozen. Consumed by
   * ActivityTimeline.resolveActivitySubject.
   */
  from_stage_slug?: string | null;
  from_stage_label?: string | null;
  to_stage_slug?: string | null;
  to_stage_label?: string | null;
  /**
   * READINESS-shift rows only (`activity_type === "readiness_shift"`, merged
   * read-time from skycrm_readiness_history, V187). Raw 4-code enum values that
   * ActivityTimeline labels; NULL at either end is meaningful (first assessment /
   * assessment cleared). Declared here even though the feed is passed straight
   * through to the timeline, so the contract is visible on the client and a
   * future field-by-field mapping cannot silently drop them.
   */
  from_readiness?: string | null;
  to_readiness?: string | null;
  /**
   * CATALYST (D1, V190) — readiness-shift rows only: the activity a counselor
   * attributed this shift to, or null when nobody has attributed it.
   *
   * ⚠️ NULL MEANS "NOBODY HAS SAID YET", never "nothing moved this". The row
   * offers the attribution affordance; it does not report a finding.
   *
   * The attributed activity's SUBJECT and the attribution's SOURCE are
   * deliberately NOT here — they ride the (much cheaper) readiness-history read
   * that the Discovery ribbon uses. This is the person timeline, and one column
   * is all it needs to pick its affordance.
   */
  catalyst_activity_id?: string | null;
  /**
   * BUG-1352 Inc-2 — the conversation THIS row belongs to. Present on projected web
   * chat rows; absent on every other row type. A chat row that HAS one opens that
   * thread; one that does not falls back per D11 (see PersonDetailPage).
   */
  session_id?: string | null;
  /**
   * Is `body` MACHINE-AUTHORED? True only on a projected web-chat row carrying a
   * `conversation_summary`, which is written only when the LLM summariser
   * succeeded. Drives the "AI-generated" caption in ActivityTimeline. Distinct
   * from `is_system_generated`, which means "a marker the system dropped".
   */
  body_is_ai_generated?: boolean | null;
}
export interface PersonActivitiesResponse {
  activities: PersonActivity[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}
export interface PersonActivitiesArgs {
  personId: string;
  instanceId: string;
  page?: number;
  limit?: number;
  /** Notes tab passes "note"; Activities tab omits (all types). */
  activityType?: string;
  /**
   * Return ONLY rows that exist in skycrm_activities — suppressing the read-time
   * merged branches (web chat conversations, inbound SMS, readiness shifts).
   *
   * For callers that hand a row's `id` back to a write endpoint: those ids are
   * session/message/history ids, not activity ids, and the server rejects them.
   * Filtering client-side is NOT equivalent — it would shrink an already-fetched
   * page rather than fill it, so the merged rows still consume the page window.
   */
  realActivitiesOnly?: boolean;
}

// ── By-session resolver (M6, Day 10 — Inbox right-rail) ─────────────
// chat_sessions.session_id → leads → skycrm_persons.external_id.
// Compact summary (NO funnel). 404 NO_CRM_LINK when unresolved.
export interface PersonSummary {
  id: string;
  first_name: string | null;
  last_name: string | null;
  score: string | null;
  score_band: ProspectRow["score_band"];
  lifecycle_status: string | null;
  primary_email: string | null;
  primary_phone: string | null;
  community_name: string | null;
  stage_name: string | null;
  stage_slug: string | null;
  stage_type: ProspectRow["stage_type"];
  stage_color: string | null;
}
export interface PersonBySessionResponse {
  person: PersonSummary;
  pinned_context: PinnedContext[];
  recent_activities: RecentActivity[];
  influencers: Influencer[];
}

// ── AI Snapshot (M7, Day 13) ────────────────────────────────────────
// Prospect-anchored synthesis. Distinct from the Inbox AIInsightsPanel
// (conversation-anchored). 5 sections, action-first order.
export interface PersonSnapshot {
  key_signals: string[];
  recommended_next_action: string;
  qualification_assessment: string;
  journey_summary: string;
  flags: string[];
  ai_generated: boolean;
}
export interface PersonSnapshotResponse {
  /**
   * G6 rider R1 — the snapshot is now behind the OPT-IN `person_snapshot` AI
   * capability. `false` means an admin has not turned it on for this account (the
   * default for any tenant with no key set). The caller must degrade QUIETLY: no
   * error toast, no empty shell. Absent on responses from an older server.
   */
  enabled?: boolean;
  snapshot: PersonSnapshot;
  /**
   * When the prose was WRITTEN, not when it was served: the cache-hit path
   * re-serves the stored payload and overrides only `cached`, so this stays the
   * original generation instant. Optional because the UI must not depend on a
   * server old enough to omit it — AISnapshotTab renders no freshness line
   * rather than an "Invalid Date" one.
   */
  generated_at?: string;
  model: string;
  cached: boolean;
  ai_generated: boolean;
}

// ── G6 surface 2 — Top-10/30 situation summary + next step ──────────
/** One paragraph on where the prospect stands + one concrete next action. */
export interface SituationSummary {
  situation: string;
  next_step: string;
  /** false = computed fallback (thin data or AI unavailable) — labeled honestly. */
  ai_generated: boolean;
}
export interface SituationSummaryResponse {
  /** false = the `situation_summary` AI capability is off for this account. */
  enabled?: boolean;
  summary?: SituationSummary;
  generated_at?: string;
  model?: string;
  cached?: boolean;
  ai_generated?: boolean;
}
/** The Top-10/30 row's rank drivers — display-only, clamped server-side. */
export interface SituationSummaryArgs {
  personId: string;
  instanceId: string;
  refresh?: boolean;
  p?: number;
  pRate?: number;
  pRecency?: number;
  pScore?: number;
}

// Wizard Inc-3 (Life Story) — one fixed category with its effective vocabulary
// (seed ∪ in-use) for the Life-Story trait pickers.
export interface ProspectTraitVocabularyCategory {
  key: string;
  label: string;
  values: { value: string; label: string }[];
}

/**
 * PLC W8 Inc-3 — the PUT …/lead-source-detail body. `null` is the CLEAR and is
 * sent as an explicit `lead_source_id: null`: dropping the key would make the
 * server 400 (the key is required), and coercing it to undefined would turn
 * every clear into a silent no-op. Pinned by crmPersonsApiLeadSourceDetail.test.ts.
 */
export function buildLeadSourceDetailBody(leadSourceId: string | null): {
  lead_source_id: string | null;
} {
  return { lead_source_id: leadSourceId };
}

export const crmPersonsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProspects: builder.query<ProspectsResponse, ProspectsArgs>({
      query: ({
        instanceId,
        tab,
        search,
        page = 1,
        limit = 25,
        stage,
        scoreBand,
        careType,
        leadSource,
        readinessStage,
        slaStatus,
        status,
        closeReason,
        daysInStageMin,
        daysInStageMax,
        noNextActivity,
        communityId,
        listId,
        segmentId,
        speedBucket,
        speedClock,
        speedPeriod,
        speedRange,
        speedFrom,
        speedTo,
        includeShared,
      }) => ({
        url: "/crm/persons",
        params: {
          instanceId,
          tab,
          ...(search ? { search } : {}),
          page,
          limit,
          ...(communityId ? { communityId } : {}),
          ...(listId ? { listId } : {}),
          // G15 — the applied segment. The route resolves + authorizes it and ANDs
          // the compiled criteria in; a malformed/invisible id fails CLOSED (400/404)
          // rather than widening to every prospect.
          ...(segmentId ? { segmentId } : {}),
          ...(speedBucket ? { speed_bucket: speedBucket } : {}),
          ...(speedClock ? { speed_clock: speedClock } : {}),
          ...(speedPeriod ? { speed_period: speedPeriod } : {}),
          ...(speedBucket && speedRange ? { range: speedRange } : {}),
          ...(speedBucket && speedRange && speedFrom ? { from: speedFrom } : {}),
          ...(speedBucket && speedRange && speedTo ? { to: speedTo } : {}),
          ...(includeShared ? { includeShared: 1 } : {}),
          // Filter-Param Contract ATOMIC-SET — only forward when at least
          // one token is selected; encode as comma-string (server parses
          // comma-split). One-element arrays produce identical URLs to the
          // pre-widening EXACT shape, preserving drill-down compat.
          ...(stage && stage.length > 0 ? { stage: stage.join(",") } : {}),
          ...(scoreBand && scoreBand.length > 0 ? { score_band: scoreBand.join(",") } : {}),
          ...(careType && careType.length > 0 ? { care_type: careType.join(",") } : {}),
          ...(leadSource && leadSource.length > 0 ? { lead_source: leadSource.join(",") } : {}),
          ...(readinessStage && readinessStage.length > 0
            ? { readiness_stage: readinessStage.join(",") }
            : {}),
          ...(slaStatus && slaStatus.length > 0 ? { sla_status: slaStatus.join(",") } : {}),
          ...(status && status.length > 0 ? { status: status.join(",") } : {}),
          ...(closeReason && closeReason.length > 0 ? { close_reason: closeReason.join(",") } : {}),
          ...(daysInStageMin ? { days_in_stage_min: daysInStageMin } : {}),
          ...(daysInStageMax ? { days_in_stage_max: daysInStageMax } : {}),
          ...(noNextActivity ? { no_next_activity: "true" } : {}),
        },
      }),
      providesTags: ["CrmPersons"],
    }),
    getPerson: builder.query<PersonDetailResponse, { id: string; instanceId: string }>({
      query: ({ id, instanceId }) => ({
        url: `/crm/persons/${id}`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { id }) => [{ type: "CrmPersons" as const, id }],
    }),
    getPersonActivities: builder.query<PersonActivitiesResponse, PersonActivitiesArgs>({
      query: ({
        personId,
        instanceId,
        page = 1,
        limit = 25,
        activityType,
        realActivitiesOnly,
      }) => ({
        url: `/crm/persons/${personId}/activities`,
        params: {
          instanceId,
          page,
          limit,
          ...(activityType ? { activity_type: activityType } : {}),
          // Suppress the read-time-merged branches (chat conversations, inbound SMS,
          // readiness shifts) so a page of N means N rows that are addressable as
          // activities. For callers that hand the id back to a write endpoint.
          ...(realActivitiesOnly ? { real_activities_only: "true" } : {}),
        },
      }),
      // Tagged by parent person id so a person-scoped invalidation
      // (future add-note mutation) refetches both tabs' pages.
      providesTags: (_r, _e, { personId }) => [{ type: "CrmPersons" as const, id: personId }],
    }),
    getPersonBySession: builder.query<
      PersonBySessionResponse,
      { sessionId: string; instanceId: string }
    >({
      query: ({ sessionId, instanceId }) => ({
        url: `/crm/persons/by-session/${sessionId}`,
        params: { instanceId },
      }),
      providesTags: (r) =>
        r ? [{ type: "CrmPersons" as const, id: r.person.id }] : ["CrmPersons"],
    }),
    getPersonSnapshot: builder.query<
      PersonSnapshotResponse,
      { personId: string; instanceId: string; refresh?: boolean }
    >({
      query: ({ personId, instanceId, refresh }) => ({
        url: `/crm/persons/${personId}/snapshot`,
        params: { instanceId, ...(refresh ? { refresh: 1 } : {}) },
      }),
      providesTags: (_r, _e, { personId }) => [{ type: "CrmPersons" as const, id: personId }],
    }),
    /**
     * G6 surface 2 — the lazy per-row situation summary. Fetched ONLY when a
     * Pipeline Review row is expanded (never 30 eager Gemini calls on board load),
     * which is why this is a separate endpoint rather than a field on the standup
     * envelope. `p*` are the row's rank drivers, forwarded so the narrative can
     * reference why the row ranks where it does.
     */
    getSituationSummary: builder.query<SituationSummaryResponse, SituationSummaryArgs>({
      query: ({ personId, instanceId, refresh, p, pRate, pRecency, pScore }) => ({
        url: `/crm/persons/${personId}/situation-summary`,
        params: {
          instanceId,
          ...(refresh ? { refresh: 1 } : {}),
          ...(p !== undefined ? { p } : {}),
          ...(pRate !== undefined ? { p_rate: pRate } : {}),
          ...(pRecency !== undefined ? { p_recency: pRecency } : {}),
          ...(pScore !== undefined ? { p_score: pScore } : {}),
        },
      }),
      providesTags: (_r, _e, { personId }) => [{ type: "CrmPersons" as const, id: personId }],
    }),

    // ── Day 22: WRITE PATHS Phase 1 (locked pattern) ──────────────────
    // invalidate-and-refetch (NOT optimistic — matches 29/30 slices).
    // Cross-surface invalidation discipline (locked, Days 22-29):
    //   person mutation   → CrmPersons:id + CrmDayPlanner
    //   activity mutation → CrmPersons:id + CrmDayPlanner + CrmEvents
    // Day Planner aggregates 5 sources; over-invalidate (refetch ~100ms)
    // beats stale data on the demo-opening view.
    // ── Prospects Depth SW1 — Add New Prospect (multi-entity create) ──
    // POST /crm/persons. Server runs the atomic CTE (prospect + 0..N
    // influencer persons + 0..N influencer_links + audit). Invalidates the
    // list + every cross-surface that counts/aggregates prospects (Day
    // Planner, Health, Overview). Returns the new prospect id for redirect.
    createProspect: builder.mutation<
      { id: string; linked_person_ids: string[]; influencer_link_ids: string[] },
      {
        instanceId: string;
        body: {
          first_name?: string;
          last_name?: string;
          // Add-Prospect depth Inc1 (B): existing-column fields surfaced at create.
          middle_name?: string;
          preferred_name?: string;
          date_of_birth?: string;
          gender?: string;
          email?: string;
          phone?: string;
          community_id: string;
          care_type?: string;
          /** PLC W8 Inc-3: the exact source — send only with `lead_source_taxonomy`. */
          lead_source_id?: string;
          lead_source?: string;
          // Free-text (shared with the Advisor); the server writes fields.timeline/budget.
          timeline?: string;
          budget?: string;
          // Wizard Inc-2 Details — JSONB-first (the server writes fields.<key>).
          marital_status?: string;
          veteran_status?: string;
          current_residence?: string;
          current_caregiver?: string;
          move_likelihood?: number;
          community_familiarity?: number;
          // Wizard Inc-3 Life Story — 4 fixed trait categories (flat fields.<key>
          // arrays) + narrative (the server writes fields.life_story).
          decision_criteria?: string[];
          care_needs?: string[];
          interests?: string[];
          interests_specify?: string[];
          life_story?: string;
          // Sales-counselor picker (B): an email from /team/assignable-users; absent
          // → owner defaults to the actor.
          assigned_to_user_id?: string;
          assigned_to_user_name?: string;
          influencers?: Array<{
            first_name?: string;
            last_name?: string;
            relationship_type: string;
            email?: string;
            phone?: string;
            // Decision unit (V168). At most ONE influencer may carry
            // is_primary — the server 400s a multi-primary payload.
            decision_role?: string;
            is_primary?: boolean;
          }>;
          // SW-CRM-PROSPECT Inc2 — "Save as draft" (incomplete record).
          draft?: boolean;
        };
      }
    >({
      query: ({ instanceId, body }) => ({
        url: "/crm/persons",
        method: "POST",
        params: { instanceId },
        body,
      }),
      invalidatesTags: ["CrmPersons", "CrmDayPlanner", "CrmHealth", "CrmOverview"],
    }),

    /**
     * Decision-unit edit on ONE influencer link (V168 write path) — the only
     * way to set primary / decision role on a contact that already exists.
     *
     * Scope is decision-unit fields only; name/email/phone edits and
     * add/remove of a contact are NOT part of this endpoint.
     *
     * Setting is_primary demotes the prospect's previous primary server-side
     * in the same transaction, so the response's `demoted_link_id` names the
     * link that lost it.
     *
     * Invalidation is the id-scoped person tag (both Influencers tabs and the
     * Discovery quadrant read from getPerson) — plus, ONLY when is_primary
     * actually moved, the broad "CrmPersons" tag, because the Prospects list
     * renders a primary-influencer sub-line that goes stale when primary moves.
     * The broad tag is deliberately conditional: in RTK a general tag already
     * invalidates every id-scoped provider of that type, so emitting it
     * unconditionally refetches the whole prospects list (and every other
     * CrmPersons-tagged query) for a decision-role-only edit that changes
     * nothing any list renders. Keyed off the response's `changed_fields`
     * rather than the request body, so a PATCH that resolves to a no-op does
     * not broadcast either.
     *
     * `residentId` is REQUIRED from the Resident page and absent from the
     * Prospect page. The resident surface renders this tab from getResident,
     * which provides {CrmResidents, id: residentId} — and RTK matches tags on
     * type AND id, so no CrmPersons tag can ever reach it. Without this the
     * dialog closes on a successful save and the card keeps showing the old
     * badge until a manual reload. The person tags stay either way: the
     * resident's person still feeds getPerson-backed surfaces.
     */
    updateInfluencerLink: builder.mutation<
      {
        id: string;
        influencer_person_id: string;
        relationship_type: string;
        decision_role: string | null;
        is_primary: boolean;
        demoted_link_id: string | null;
        changed_fields: string[];
      },
      {
        instanceId: string;
        personId: string;
        linkId: string;
        /** Set ONLY on the Resident page — see the invalidation note above. */
        residentId?: string;
        body: {
          // null CLEARS the role ("not recorded"), distinct from 'none'.
          decision_role?: string | null;
          is_primary?: boolean;
        };
      }
    >({
      query: ({ instanceId, personId, linkId, body }) => ({
        url: `/crm/persons/${personId}/influencers/${linkId}`,
        method: "PATCH",
        params: { instanceId },
        body,
      }),
      invalidatesTags: (result, _e, { personId, residentId, body }) => {
        // Fall back to the request body only if the response is missing — the
        // server is the authority on what actually moved.
        const primaryMoved = result
          ? result.changed_fields.includes("is_primary")
          : body.is_primary !== undefined;
        return [
          { type: "CrmPersons" as const, id: personId },
          ...(primaryMoved ? (["CrmPersons"] as const) : []),
          ...(residentId ? [{ type: "CrmResidents" as const, id: residentId }] : []),
        ];
      },
    }),

    // Wizard Inc-3 (Life Story) — effective per-category trait vocabulary (seed ∪
    // in-use), data-driven so the multi-selects grow with no deploy. Mirrors the
    // referrer trait-vocabulary. The id-scoped tag is covered by createProspect's
    // "CrmPersons" invalidation, so a newly-minted value re-fetches after a create.
    getProspectTraitVocabulary: builder.query<
      { categories: ProspectTraitVocabularyCategory[] },
      { instanceId: string }
    >({
      query: ({ instanceId }) => ({
        url: `/crm/persons/trait-vocabulary`,
        params: { instanceId },
      }),
      providesTags: [{ type: "CrmPersons" as const, id: "trait-vocabulary" }],
    }),

    // SW-CRM-PROSPECT cross-community Share v1 — the share set for a prospect.
    getProspectShares: builder.query<
      { shares: Array<{ community_id: string; community_name: string | null; shared_at: string }> },
      { instanceId: string; personId: string }
    >({
      query: ({ instanceId, personId }) => ({
        url: `/crm/persons/${personId}/shares`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { personId }) => [{ type: "ProspectShares" as const, id: personId }],
    }),
    // Share a prospect INTO a community (all-access only; server 403s restricted callers).
    shareProspect: builder.mutation<
      { shared: boolean; community_id: string },
      { instanceId: string; personId: string; communityId: string }
    >({
      query: ({ instanceId, personId, communityId }) => ({
        url: `/crm/persons/${personId}/shares`,
        method: "POST",
        params: { instanceId },
        body: { community_id: communityId },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "ProspectShares" as const, id: personId },
        { type: "CrmPersons" as const, id: personId },
        "CrmPersons",
      ],
    }),
    // Un-share (narrow); all-access or the target-community counselor removing own inbound.
    unshareProspect: builder.mutation<
      { unshared: boolean; community_id: string },
      { instanceId: string; personId: string; communityId: string }
    >({
      query: ({ instanceId, personId, communityId }) => ({
        url: `/crm/persons/${personId}/shares/${communityId}`,
        method: "DELETE",
        params: { instanceId },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "ProspectShares" as const, id: personId },
        { type: "CrmPersons" as const, id: personId },
        "CrmPersons",
      ],
    }),
    // SW-CRM-PROSPECT Inc2 — resume → finish: flip a draft prospect to active.
    promoteDraft: builder.mutation<
      { person_id: string; lifecycle_status: string; promoted: boolean },
      { personId: string; instanceId: string }
    >({
      query: ({ personId, instanceId }) => ({
        url: `/crm/persons/${personId}/promote-draft`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: ["CrmPersons", "CrmOverview"],
    }),
    // SW-CRM-PROSPECT Inc2 — follow / unfollow (personal "Followed" list).
    followProspect: builder.mutation<
      { person_id: string; following: boolean },
      { personId: string; instanceId: string }
    >({
      query: ({ personId, instanceId }) => ({
        url: `/crm/persons/${personId}/follow`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: ["CrmPersons"],
    }),
    unfollowProspect: builder.mutation<
      { person_id: string; following: boolean },
      { personId: string; instanceId: string }
    >({
      query: ({ personId, instanceId }) => ({
        url: `/crm/persons/${personId}/follow`,
        method: "DELETE",
        params: { instanceId },
      }),
      invalidatesTags: ["CrmPersons"],
    }),
    addNote: builder.mutation<
      { note: PersonActivity },
      { personId: string; instanceId: string; body: string; subject?: string }
    >({
      query: ({ personId, instanceId, body, subject }) => ({
        url: `/crm/persons/${personId}/notes`,
        method: "POST",
        params: { instanceId },
        body: { body, ...(subject ? { subject } : {}) },
      }),
      // A note IS an activity → also refreshes the Events surface.
      // CrmHealth (Day 30): note bodies don't change duplicate /
      // stale / missing-contact signals directly, but the activity
      // INSERT does refresh the stale predicate (last_activity_at).
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmDayPlanner",
        "CrmEvents",
        "CrmHealth",
      ],
    }),
    updatePerson: builder.mutation<
      { id: string; updated: boolean },
      {
        personId: string;
        instanceId: string;
        patch: Partial<{
          salutation: string | null;
          preferred_name: string | null;
          gender: string | null;
          email: string;
          phone: string;
          care_type: string;
          timeline_urgency: string;
          budget_band: string;
          community_id: string | null;
          do_not_call: boolean;
          do_not_text: boolean;
          do_not_email: boolean;
          do_not_mail: boolean;
          // Detail-depth Inc-3 — editable Details + Life Story. All land in the
          // fields JSONB via the shallow || merge (server accepts these keys).
          // Assessments are "1".."5" strings (server z.coerce.number); traits are
          // flat per-category arrays; life_story is rich HTML.
          marital_status: string | null;
          veteran_status: string | null;
          current_residence: string | null;
          current_caregiver: string | null;
          move_likelihood: string | null;
          community_familiarity: string | null;
          decision_criteria: string[];
          care_needs: string[];
          interests: string[];
          interests_specify: string[];
          life_story: string | null;
          // E3 SLA — dismiss the ReturningProspectBanner. Null-ONLY: clients
          // can only CLEAR the job-written fields.returning_prospect key
          // (the server schema accepts z.null(), never a value).
          returning_prospect: null;
          // Phase-1 custom-field runtime (V113): pack-declared field VALUES keyed
          // by slug (a null value clears one). Server role-gates + type-validates
          // against the resolved defs; stored in the custom_data JSONB.
          custom_data: Record<string, unknown>;
        }>;
      }
    >({
      query: ({ personId, instanceId, patch }) => ({
        url: `/crm/persons/${personId}`,
        method: "PATCH",
        params: { instanceId },
        body: patch,
      }),
      // CrmHealth (Day 30): PATCH person can edit email/phone fields
      // → potentially creates or resolves duplicate / missing-contact
      // signals. Invalidate to refresh counts.
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmDayPlanner",
        "CrmHealth",
      ],
    }),

    // Reassign a prospect's owner. The picker lists users by email
    // (assignedToUserId = email from /team/assignable-users); the server
    // resolves it to a CRM owner-person. null clears the owner (Unassigned).
    // Invalidate the detail + list + Day Planner (the "my" tabs key off owner).
    assignPersonOwner: builder.mutation<
      { id: string; updated: boolean },
      {
        personId: string;
        instanceId: string;
        assignedToUserId: string | null;
        assignedToUserName: string | null;
      }
    >({
      query: ({ personId, instanceId, assignedToUserId, assignedToUserName }) => ({
        url: `/crm/persons/${personId}/owner`,
        method: "PUT",
        params: { instanceId },
        body: { assignedToUserId, assignedToUserName },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmPersons",
        "CrmDayPlanner",
        // Ownership decides WHOSE feed a prospect appears in, so a reassignment
        // must drop the cached recommendations on both sides.
        "CrmNbaFeed",
      ],
    }),

    // ── Day 23: WRITE PATHS Phase 2 (cross-entity, locked CTE) ────────
    // Stage transition = person UPDATE + stage_history INSERT +
    // system_event activity, one atomic data-modifying-CTE statement.
    // Invalidation (ratified): {CrmPersons:id} + CrmPersons (the list
    // reflects stage / stale / tour-scheduled tabs) + CrmDayPlanner +
    // CrmEvents (the new system_event activity).
    transitionStage: builder.mutation<
      {
        person_id: string;
        from_stage_id: string | null;
        to_stage_id: string;
        transitioned: boolean;
      },
      {
        personId: string;
        instanceId: string;
        to_stage_id: string;
        reason?: string;
        note?: string;
        /** SW-LIFECYCLE: optional actual move-in date for the move_in dispatch. */
        move_in_date?: string;
      }
    >({
      query: ({ personId, instanceId, to_stage_id, reason, note, move_in_date }) => ({
        url: `/crm/persons/${personId}/stage-transitions`,
        method: "POST",
        params: { instanceId },
        body: {
          to_stage_id,
          ...(reason ? { reason } : {}),
          ...(note ? { note } : {}),
          ...(move_in_date ? { move_in_date } : {}),
        },
      }),
      // CrmHealth (Day 30): a stage transition is a fresh activity
      // (system_event) → updates the last_activity_at signal that
      // backs the stale predicate. Invalidate.
      //
      // Day-5: also invalidates CrmOverview — the Sales Funnel widget
      // counts cohort members by max stage reached, so a stage
      // transition changes the funnel shape and ratios.
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmPersons",
        "CrmDayPlanner",
        "CrmEvents",
        "CrmHealth",
        "CrmOverview",
        // Lifecycle and stage writes decide whether a prospect belongs in the feed
        // at all (a closed or deceased prospect should stop being recommended) and
        // move fallout_risk / no_next_step for the ones that remain.
        "CrmNbaFeed",
        // a stage advance (move-in/reserve) changes a unit's occupancy → refresh the Units list +
        // the delete gray-out (open_assignment_count), and the Occupancy board.
        "CrmUnits",
        "CrmOccupancy",
      ],
    }),
    // Add Referrer Attribution = person UPDATE + system_event
    // activity. Invalidation (ratified): {CrmPersons:id} +
    // {CrmReferrers:id} (the org's referred-prospects / ROI) +
    // CrmDayPlanner + CrmEvents. No bare CrmPersons (attribution
    // isn't a Prospects-list column).
    setAttribution: builder.mutation<
      { person_id: string; primary_source_id: string | null; attributed: boolean },
      {
        personId: string;
        instanceId: string;
        // REF-10 p2 — PERSON-FIRST: the referrer is a person (skycrm_referrer_persons).
        // The org is DERIVED server-side from the person's link; no org input.
        primary_source_person_id: string;
      }
    >({
      query: ({ personId, instanceId, primary_source_person_id }) => ({
        url: `/crm/persons/${personId}/attribution`,
        method: "POST",
        params: { instanceId },
        body: { primary_source_person_id },
      }),
      // CrmHealth (Day 30): attribution writes a system_event activity
      // → last_activity_at refreshes (stale signal). Invalidate.
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmReferrers",
        "CrmReferrerPersons",
        "CrmDayPlanner",
        "CrmEvents",
        "CrmHealth",
      ],
    }),
    // ── SW-LIFECYCLE — Unit & Deposit section ──────────────────────────
    // Record a unit-linked reservation deposit (Part 1). Invalidates the
    // person (the deposit shows in its detail) + CrmOccupancy (the picker's
    // derived badges) + the activity/health signals.
    recordDeposit: builder.mutation<
      { deposit_id: string },
      {
        personId: string;
        instanceId: string;
        unit_id: string;
        amount: number;
        received_on?: string;
        status?: "received" | "cleared";
        counselor_person_id?: string | null;
        is_second_occupant?: boolean;
        move_in_date?: string | null;
      } & DepositTermsBody
    >({
      query: ({ personId, instanceId, ...body }) => ({
        url: `/crm/persons/${personId}/deposits`,
        method: "POST",
        params: { instanceId },
        body,
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmOccupancy",
        "CrmEvents",
        "CrmHealth",
        // a deposit reserves a unit → refresh the Units list + delete gray-out.
        "CrmUnits",
        // Recording a deposit supplies the unit and move-in date a Yardi push
        // requires, so it changes the answer the Sync button is showing. Without
        // this the button stays disabled, still naming fields the operator has
        // just filled in, until the page is reloaded.
        "YardiIntegrations",
      ],
    }),
    // Manual score-band override (Inc1). Sets score_band + stamps the override
    // marker; invalidates the person so the header re-renders with the marker.
    overrideScore: builder.mutation<
      { person_id: string; score_band: "hot" | "warm" | "cool"; overridden: boolean },
      // null CLEARS the manual override and resumes automatic scoring.
      { personId: string; instanceId: string; scoreBand: "hot" | "warm" | "cool" | null }
    >({
      query: ({ personId, instanceId, scoreBand }) => ({
        url: `/crm/persons/${personId}/score`,
        method: "PATCH",
        params: { instanceId },
        body: { score_band: scoreBand },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmPersons",
        "CrmOverview",
      ],
    }),
    // E5 Inc-1: flip the per-prospect nurture automation kill-switch (D6/D13).
    // Feature-gated on "nurture"; community-scoped + audited server-side.
    setAutomationPaused: builder.mutation<
      { personId: string; automation_paused: boolean },
      { personId: string; instanceId: string; paused: boolean }
    >({
      query: ({ personId, instanceId, paused }) => ({
        url: `/crm/persons/${personId}/automation`,
        method: "PUT",
        params: { instanceId },
        body: { paused },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmPersons",
      ],
    }),
    // Refund (partial or full) against a recorded deposit (Inc1, MONEY op).
    // Server caps at the remaining refundable + audits deposit.refunded.
    // Invalidates the person so the deposit summary re-renders with the new
    // refunded amount.
    refundDeposit: builder.mutation<
      { deposit_id: string; total_refunded: number; remaining_refundable: number },
      { personId: string; instanceId: string; depositId: string; amount: number }
    >({
      query: ({ personId, instanceId, depositId, amount }) => ({
        url: `/crm/persons/${personId}/deposits/${depositId}/refund`,
        method: "POST",
        params: { instanceId },
        body: { amount },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmOccupancy",
      ],
    }),
    // Correct the INTENDED move-in date on a recorded reservation deposit.
    //
    // It was write-once — captured on the deposit form and nowhere else — and it
    // gates the Yardi push, so missing it there left the sync permanently
    // unreachable. Nullable: clearing is how a commitment is retracted.
    updateDepositMoveInDate: builder.mutation<
      { deposit_id: string; move_in_date: string | null; changed: boolean },
      { personId: string; instanceId: string; depositId: string; moveInDate: string | null }
    >({
      query: ({ personId, instanceId, depositId, moveInDate }) => ({
        url: `/crm/persons/${personId}/deposits/${depositId}/move-in-date`,
        method: "PATCH",
        params: { instanceId },
        body: { move_in_date: moveInDate },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmOccupancy",
        // Same reason as the lease answer below: a missing move-in date is one
        // of the things readiness refuses on, so setting it has to refresh that
        // query or the Sync button stays disabled for a reason since fixed.
        { type: "YardiIntegrations" as const, id: `readiness:${personId}` },
      ],
    }),
    // PLC W9 Inc-5 — edit the QUOTED rate / concessions on a reservation
    // deposit before the reservation (after it the stay owns them: 409
    // TERMS_HELD_BY_STAY, and the edit goes through Edit terms instead).
    // Only changed keys are sent; null clears.
    updateDepositTerms: builder.mutation<
      { deposit_id: string; updated: string[] },
      { personId: string; instanceId: string; depositId: string } & DepositTermsBody
    >({
      query: ({ personId, instanceId, depositId, ...body }) => ({
        url: `/crm/persons/${personId}/deposits/${depositId}/terms`,
        method: "PATCH",
        params: { instanceId },
        body,
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        // Report runs (the rent roll's deposit-hold view, the Concessions
        // report) are recomputed on next open.
        "CrmReportRun",
      ],
    }),
    // Correct who holds the lease on a recorded reservation deposit.
    //
    // The flag was previously write-once — captured on the deposit form with no
    // route to move it — so an operator who left it unticked had no way back,
    // and the Yardi push refused permanently. Invalidates the person (the
    // section re-renders) and CrmOccupancy (the flag changes the bed count).
    updateDepositOccupancyRole: builder.mutation<
      { deposit_id: string; is_second_occupant: boolean; changed: boolean },
      { personId: string; instanceId: string; depositId: string; isSecondOccupant: boolean }
    >({
      query: ({ personId, instanceId, depositId, isSecondOccupant }) => ({
        url: `/crm/persons/${personId}/deposits/${depositId}/occupancy-role`,
        method: "PATCH",
        params: { instanceId },
        body: { is_second_occupant: isSecondOccupant },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmOccupancy",
        // The Yardi READINESS query is what disables "Sync to Yardi" and writes
        // its tooltip, and it is what the blocked-state prompt reads. Without
        // this it never refetched, so answering the lease question changed the
        // deposit and left the prompt on screen with the button still disabled —
        // indistinguishable from the save failing. Reported exactly that way.
        { type: "YardiIntegrations" as const, id: `readiness:${personId}` },
      ],
    }),
    // Cancel a reservation (Part 4) — releases the unit hold; reverts stage.
    cancelReservation: builder.mutation<
      {
        person_id: string;
        cancelled: boolean;
        resident_id: string;
        unit_id: string;
        deposit_id: string | null;
        reverted_to_stage_id: string | null;
      },
      { personId: string; instanceId: string }
    >({
      query: ({ personId, instanceId }) => ({
        url: `/crm/persons/${personId}/cancel-reservation`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmPersons",
        "CrmOccupancy",
        "CrmDayPlanner",
        "CrmEvents",
        "CrmHealth",
        "CrmOverview",
        // Lifecycle and stage writes decide whether a prospect belongs in the feed
        // at all (a closed or deceased prospect should stop being recommended) and
        // move fallout_risk / no_next_step for the ones that remain.
        "CrmNbaFeed",
        // cancelling a reservation frees a unit → refresh the Units list + delete gray-out.
        "CrmUnits",
      ],
    }),
    // Generic one-stage-back revert (Part 4).
    revertStage: builder.mutation<
      {
        person_id: string;
        reverted: boolean;
        from_stage_id: string;
        to_stage_id: string;
        to_slug: string | null;
      },
      { personId: string; instanceId: string }
    >({
      query: ({ personId, instanceId }) => ({
        url: `/crm/persons/${personId}/revert-stage`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmPersons",
        "CrmDayPlanner",
        "CrmEvents",
        "CrmHealth",
        "CrmOverview",
        // Lifecycle and stage writes decide whether a prospect belongs in the feed
        // at all (a closed or deceased prospect should stop being recommended) and
        // move fallout_risk / no_next_step for the ones that remain.
        "CrmNbaFeed",
      ],
    }),
    // SW-CRM-LIFECYCLE — terminal close / reopen / mark-deceased.
    closeProspect: builder.mutation<
      {
        person_id: string;
        closed: boolean;
        stage_id: string;
        reason: string;
        /** SW-CRM-WAITLIST v1.2: entries auto-removed on close (null = attempt failed). */
        waitlist_removed: number | null;
      },
      {
        personId: string;
        instanceId: string;
        close_reason_id: string;
        note?: string;
        /** PLC W7 Inc-3: a listed competitor (uuid) or null for "Other / not listed".
         *  Omit for any reason that is not a 'competitor' reason. */
        competitor_id?: string | null;
        /** PLC W7 Inc-4: the option code of a 'choice' reason. Omit otherwise. */
        detail_code?: string;
        /** WH parity: default-remove from waitlist; true keeps the entries. */
        keep_on_waitlist?: boolean;
      }
    >({
      query: ({
        personId,
        instanceId,
        close_reason_id,
        note,
        competitor_id,
        detail_code,
        keep_on_waitlist,
      }) => ({
        url: `/crm/persons/${personId}/close`,
        method: "POST",
        params: { instanceId },
        body: {
          close_reason_id,
          ...(note ? { note } : {}),
          ...(competitor_id !== undefined ? { competitor_id } : {}),
          ...(detail_code !== undefined ? { detail_code } : {}),
          ...(keep_on_waitlist !== undefined ? { keep_on_waitlist } : {}),
        },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmPersons",
        "CrmDayPlanner",
        "CrmEvents",
        "CrmHealth",
        "CrmOverview",
        // Lifecycle and stage writes decide whether a prospect belongs in the feed
        // at all (a closed or deceased prospect should stop being recommended) and
        // move fallout_risk / no_next_step for the ones that remain.
        "CrmNbaFeed",
        "CrmWaitlist",
      ],
    }),
    markDeceased: builder.mutation<
      { person_id: string; deceased: boolean },
      { personId: string; instanceId: string; note?: string }
    >({
      query: ({ personId, instanceId, note }) => ({
        url: `/crm/persons/${personId}/mark-deceased`,
        method: "POST",
        params: { instanceId },
        body: note ? { note } : {},
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmPersons",
        "CrmDayPlanner",
        "CrmEvents",
        "CrmHealth",
        "CrmOverview",
        // Lifecycle and stage writes decide whether a prospect belongs in the feed
        // at all (a closed or deceased prospect should stop being recommended) and
        // move fallout_risk / no_next_step for the ones that remain.
        "CrmNbaFeed",
      ],
    }),
    reopenProspect: builder.mutation<
      { person_id: string; reopened: boolean; restored_stage_id: string },
      { personId: string; instanceId: string }
    >({
      query: ({ personId, instanceId }) => ({
        url: `/crm/persons/${personId}/reopen`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmPersons" as const, id: personId },
        "CrmPersons",
        "CrmDayPlanner",
        "CrmEvents",
        "CrmHealth",
        "CrmOverview",
        // Lifecycle and stage writes decide whether a prospect belongs in the feed
        // at all (a closed or deceased prospect should stop being recommended) and
        // move fallout_risk / no_next_step for the ones that remain.
        "CrmNbaFeed",
      ],
    }),
    /**
     * PLC W8 Inc-3 — PUT /crm/persons/:id/lead-source-detail. `leadSourceId:
     * null` is sent explicitly: it is the CLEAR, not an omission.
     */
    setPersonLeadSourceDetail: builder.mutation<
      {
        person_id: string;
        lead_source_id: string | null;
        category_filled: string | null;
        updated: boolean;
      },
      { personId: string; instanceId: string; leadSourceId: string | null }
    >({
      query: ({ personId, instanceId, leadSourceId }) => ({
        url: `/crm/persons/${personId}/lead-source-detail`,
        method: "PUT",
        params: { instanceId },
        body: buildLeadSourceDetailBody(leadSourceId),
      }),
      invalidatesTags: (_r, _e, { personId }) => [{ type: "CrmPersons" as const, id: personId }],
    }),
    // PLC W7 Inc-1 (D10b): the audited after-the-fact edit of the close note.
    // Server allows it only while closed_lost / deceased; NOTE_REQUIRED 400 when
    // the reason is free_text and the note is empty.
    updateProspectCloseNote: builder.mutation<
      { person_id: string; close_note: string | null; updated: boolean },
      { personId: string; instanceId: string; note: string | null }
    >({
      query: ({ personId, instanceId, note }) => ({
        url: `/crm/persons/${personId}/close-note`,
        method: "PATCH",
        params: { instanceId },
        // `note: null` is sent explicitly — it is the CLEAR, not an omission.
        body: { note },
      }),
      invalidatesTags: (_r, _e, { personId }) => [{ type: "CrmPersons" as const, id: personId }],
    }),
  }),
});

export const {
  useGetProspectsQuery,
  useGetPersonQuery,
  useGetPersonActivitiesQuery,
  useGetPersonBySessionQuery,
  useGetPersonSnapshotQuery,
  useGetSituationSummaryQuery,
  useCreateProspectMutation,
  useUpdateInfluencerLinkMutation,
  useGetProspectTraitVocabularyQuery,
  useGetProspectSharesQuery,
  useShareProspectMutation,
  useUnshareProspectMutation,
  usePromoteDraftMutation,
  useFollowProspectMutation,
  useUnfollowProspectMutation,
  useAddNoteMutation,
  useUpdatePersonMutation,
  useAssignPersonOwnerMutation,
  useTransitionStageMutation,
  useSetAttributionMutation,
  useOverrideScoreMutation,
  useSetAutomationPausedMutation,
  useRecordDepositMutation,
  useRefundDepositMutation,
  useUpdateDepositOccupancyRoleMutation,
  useUpdateDepositMoveInDateMutation,
  useUpdateDepositTermsMutation,
  useCancelReservationMutation,
  useRevertStageMutation,
  useCloseProspectMutation,
  useMarkDeceasedMutation,
  useReopenProspectMutation,
  useUpdateProspectCloseNoteMutation,
  useSetPersonLeadSourceDetailMutation,
} = crmPersonsApi;
